<template>
  <div class="analyze-location-play">
    <!-- 実行前 -->
    <div v-if="!isAnalyzeShown" class="before-start-contents">
      <div class="params-container">
        <analyze-params-selector
          v-bind="params"
          v-bind:period-presets="periodPresets"
          v-bind:max-hours="maxHours"
          v-bind:max-users-count="maxUsersCount"
          v-bind:period-state="isPeriodWithinMaxHours"
          v-on="$listeners"
        />
      </div>
      <analyze-target-user-list
        class="user-list"
        v-bind:users="users"
        v-bind:state="isUsersWithinMaxCount"
        v-bind:max-users-count="maxUsersCount"
        v-on="$listeners"
      />
      <div class="execute-container">
        <b-button
          v-bind:variant="isExecuteable ? 'success' : 'secondary'"
          v-bind:disabled="!isExecuteable"
          class="execute-button"
          v-t="'appAnalyzeLocationPlay.action.execute'"
          v-on:click="$_execute"
        />
      </div>
    </div>
    <!-- 実行後 -->
    <div v-else class="after-start-contents">
      <div class="back-row" v-on:click="$_backToSetting">
        <arrow-down class="back-arrow" />
        <span class="back-text" v-t="'appAnalyzeLocationPlay.action.back'" />
      </div>
      <div class="params-container">
        <card-analyze-params v-bind="params" />
      </div>
      <b-row class="title-bar">
        <h1 class="legend-title" v-t="'appAnalyzeLocationPlay.legend'" />
        <span
          class="action"
          v-if="hasHiddenUsers"
          v-on:click="visible.user = true"
        >
          {{ $t('appAnalyzeLocationPlay.action.resetVisibility') }}
        </span>
      </b-row>
      <loading-mask v-bind:is-loading="isLoading">
        <div class="legend-container">
          <analyze-legend-location-play
            class="legend center"
            v-bind="legend"
            v-bind:visible="visible"
            v-on="$listeners"
          />
        </div>
      </loading-mask>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import AnalyzeParamsSelector from '@/components/analyze/AppAnalyzeParamsSelector.vue'
import AnalyzeTargetUserList from '@/components/analyze/AppAnalyzeTargetUserList.vue'
import CardAnalyzeParams from '@/components/analyze/AppCardAnalyzeParams.vue'
import AnalyzeLegendLocationPlay from '@/components/analyze/AppAnalyzeLegendLocationPlay.vue'
import ArrowDown from '@/assets/ifield/icon_arrow_down.svg?component'
import { DATETIME_FORMAT_API } from '@/constants.js'

export default {
  name: 'AppAnalyzeLocationPlay',
  components: {
    'analyze-params-selector': AnalyzeParamsSelector,
    'analyze-target-user-list': AnalyzeTargetUserList,
    'card-analyze-params': CardAnalyzeParams,
    'analyze-legend-location-play': AnalyzeLegendLocationPlay,
    'arrow-down': ArrowDown,
  },
  props: {
    /**
     * 開始日時
     */
    from: {
      type: String,
      default: null,
    },
    /**
     * 終了日時
     */
    to: {
      type: String,
      default: null,
    },
    /**
     * 対象ユーザー
     */
    users: {
      type: Array,
      default: null,
    },
    /**
     * 屋内ビル
     */
    indoorBuildings: {
      type: Array,
      default: null,
    },
    /**
     * 表示状態
     */
    visible: {
      type: Object,
      default: () => ({ user: true }),
    },
    /**
     * 分析表示中はtrue
     */
    isAnalyzeShown: {
      type: Boolean,
      default: false,
    },
    /**
     * 凡例用情報
     */
    legend: {
      type: Object,
      default: () => {},
    },
    /**
     * 最大指定期間(時間)
     */
    maxHours: {
      type: Number,
      default: 24,
    },
    /**
     * 最大ユーザー数
     */
    maxUsersCount: {
      type: Number,
      default: 30,
    },
    /**
     * 非表示対象ユーザーがいる場合はtrue
     */
    hasHiddenUsers: {
      type: Boolean,
      default: false,
    },
    /**
     * ローディング状態
     */
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    /**
     * パラメータ
     * @type {Object}
     */
    params: function () {
      return {
        from: this.from,
        to: this.to,
        users: this.users,
        indoorBuildings: this.indoorBuildings,
      }
    },
    /**
     * 期間選択のプリセット
     * @type {Array.<Object>}
     */
    periodPresets: function () {
      return [
        {
          value: 1,
          text: this.$t('appAnalyzeLocationPlay.period.today'),
          from: moment(this.now).startOf('day').format(DATETIME_FORMAT_API),
          to: moment(this.now).endOf('day').format(DATETIME_FORMAT_API),
        },
        {
          value: 2,
          text: this.$t('appAnalyzeLocationPlay.period.oneDayAgo'),
          from: moment(this.now)
            .subtract(1, 'days')
            .startOf('day')
            .format(DATETIME_FORMAT_API),
          to: moment(this.now)
            .subtract(1, 'days')
            .endOf('day')
            .format(DATETIME_FORMAT_API),
        },
        {
          value: 3,
          text: this.$t('appAnalyzeLocationPlay.period.twoDaysAgo'),
          from: moment(this.now)
            .subtract(2, 'days')
            .startOf('day')
            .format(DATETIME_FORMAT_API),
          to: moment(this.now)
            .subtract(2, 'days')
            .endOf('day')
            .format(DATETIME_FORMAT_API),
        },
        {
          value: 4,
          text: this.$t('appAnalyzeLocationPlay.period.oneHourAgo'),
          from: moment(this.now)
            .subtract(1, 'hours')
            .format(DATETIME_FORMAT_API),
          to: moment(this.now).format(DATETIME_FORMAT_API),
        },
        {
          value: 5,
          text: this.$t('appAnalyzeLocationPlay.period.tenMinutesAgo'),
          from: moment(this.now)
            .subtract(10, 'minutes')
            .format(DATETIME_FORMAT_API),
          to: moment(this.now).format(DATETIME_FORMAT_API),
        },
      ]
    },
    /**
     * 期間がmax以内ならtrue
     * @type {Boolean}
     */
    isPeriodWithinMaxHours: function () {
      const from = moment(this.from)
      const to = moment(this.to)
      // 不正日時はfalse
      if (!from.isValid() || !to.isValid()) return false
      // 逆転・同値はfalse
      if (to.isSameOrBefore(from)) return false
      return to.subtract(this.maxHours, 'hours').isBefore(from)
    },
    /**
     * 選択ユーザー数
     * @type {number}
     */
    userCount: function () {
      if (!this.users) return 0
      const userIdSet = new Set()
      for (const { type, id, users } of this.users) {
        if (type === 'user') {
          userIdSet.add(id)
        } else if (type === 'organization') {
          users.forEach((u) => userIdSet.add(u.id))
        }
      }
      return userIdSet.size
    },
    /**
     * ユーザー数がmax以内ならtrue
     * @type {Boolean}
     */
    isUsersWithinMaxCount: function () {
      return this.userCount <= this.maxUsersCount
    },
    /**
     * 分析実行可能な場合はtrue
     * @type {Boolean}
     */
    isExecuteable: function () {
      // 24時間以内
      const isPeriodOk = this.isPeriodWithinMaxHours
      // ユーザーが1人以上10人以内
      const isUserOk = this.userCount > 0 && this.isUsersWithinMaxCount
      // ビルが1つ以下
      const isBuildingOk =
        !this.params.indoorBuildings || this.params.indoorBuildings.length <= 1
      return isPeriodOk && isUserOk && isBuildingOk
    },
  },
  methods: {
    /**
     * 分析実行
     */
    $_execute: function () {
      /**
       * 分析表示イベント
       */
      this.$emit('analyze-show')
    },

    /**
     * パラメータ設定に戻る
     */
    $_backToSetting: function () {
      /**
       * 分析非表示イベント
       */
      this.$emit('analyze-hide')
    },
    /**
     * 全てのユーザーを表示する
     */
    $_showAllUsers: function () {
      this.$emit('user-visible-changed', true)
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/parts/title-bar';

$-execute-container-height: 64px;

.analyze-location-play {
  width: 100%;
  height: 100%;
}

.before-start-contents {
  height: 100%;
  display: grid;
  grid-template-rows: max-content minmax(0, 1fr) $-execute-container-height;
  grid-auto-columns: minmax(0, 1fr);
  row-gap: 0;
}

.params-container {
  width: 100%;
  padding: 16px 8px 8px 8px;
}

.user-list {
  width: 100%;
  border-top: 2px solid #eaecef;
  border-bottom: 2px solid #eaecef;
}

.execute-container {
  width: 100%;
  padding: 16px 0;
  display: flex;
  justify-content: center;
  align-items: center;

  .execute-button {
    height: 2.5rem;
    width: 7.5rem;
    font-size: $font-size-middle;
    background-color: $entity-color-green;

    &.disabled {
      background-color: $entity-color-light-gray;
      border: none;
    }
  }
}

.after-start-contents {
  height: 100%;
  display: grid;
  grid-template-rows: max-content max-content $title-bar-height minmax(0, 1fr);
  grid-auto-columns: minmax(0, 1fr);
  row-gap: 0;
}

.back-row {
  width: fit-content;
  margin: 16px 0 0 16px;
  padding: 0;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  column-gap: 4px;
  cursor: pointer;

  &:hover {
    background: linear-gradient(
      transparent calc(100% - 1px),
      $entity-color-green 1px
    );
  }

  .back-arrow {
    width: 32px;
    height: 32px;
    margin: -8px;
    fill: none;
    stroke: $entity-color-green;
    -webkit-transform: rotate(90deg);
    -moz-transform: rotate(90deg);
    -ms-transform: rotate(90deg);
    -o-transform: rotate(90deg);
    transform: rotate(90deg);
  }

  .back-text {
    color: $entity-color-green;
    line-height: 1;
    font-size: $font-size-middle;
    font-weight: $font-weight-bold;
  }
}

.title-bar {
  border-top: 2px solid #eaecef;
  border-bottom: 2px solid #eaecef;

  .action {
    color: $text-color-weak;
    font-size: $font-size-small;
    text-decoration: underline;
    cursor: pointer;
    flex-grow: 1;
    text-align: right;
  }

  .legend-title {
    font-size: $font-size-middle;
    font-weight: $font-weight-normal;
  }
}

.legend-container {
  height: 100%;
  width: 100%;
  overflow-y: auto;
  @include mixin-scrollbar;

  .legend {
    margin: 40px 64px;

    &.center {
      margin: 16px 0;
      padding: 0 32px;
      justify-content: center;
    }
  }
}
</style>
